import * as React from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Link as LinkIcon, X } from 'lucide-react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { env } from '@/lib/env';
import { cn } from '@/lib/cn';
import type {
  ContentOrder,
  OrderStage,
  DeliveredUrl,
  OrderState,
} from '@/lib/content';

interface OperatorOrder extends ContentOrder {
  tenant_slug: string | null;
  tenant_legal_name: string | null;
}

export const Route = createFileRoute('/_app/operator/content-orders')({
  staticData: { crumb: 'Zamówienia content' },
  beforeLoad: ({ context }) => {
    const ctx = context as { session: { role: string } };
    if (ctx.session.role !== 'operator') throw redirect({ to: '/' });
  },
  component: OperatorContentOrders,
});

const STAGE_OPTIONS: OrderStage[] = [
  'pending',
  'in_progress',
  'partial',
  'delivered',
  'indexed',
  'failed',
];

const STAGE_LABEL: Record<OrderStage, string> = {
  pending: 'Czeka',
  in_progress: 'W realizacji',
  partial: 'Częściowo dostarczone',
  delivered: 'Dostarczone',
  indexed: 'Zaindeksowane',
  failed: 'Niepowodzenie',
};

const STATE_LABEL: Record<OrderState, string> = {
  requested: 'requested',
  active: 'active',
  paused: 'paused',
  cancelled: 'cancelled',
  completed: 'completed',
};

function OperatorContentOrders() {
  const [stageFilter, setStageFilter] = React.useState<OrderStage | ''>('');
  const orders = useQuery({
    queryKey: ['operator', 'content-orders', stageFilter],
    queryFn: async (): Promise<OperatorOrder[]> => {
      const url = new URL(`${env.apiBaseUrl}/api/operator/content/orders`, window.location.origin);
      if (stageFilter) url.searchParams.set('stage', stageFilter);
      const res = await fetch(url.toString(), { credentials: 'include' });
      if (!res.ok) throw new Error('failed_to_load');
      const j = (await res.json()) as { orders: OperatorOrder[] };
      return j.orders;
    },
    refetchOnWindowFocus: true,
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">
          Zamówienia content
        </h1>
        <p className="mt-1 text-sm text-text-muted">
          Wszystkie zamówienia content marketplace ze wszystkich klinik. Aktualizuj
          stage + wpisuj dostarczone URL-e — zmiany od razu widoczne w panelu klienta.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filtry</CardTitle>
          <CardDescription>Domyślnie pokazujemy wszystkie aktywne stadia.</CardDescription>
        </CardHeader>
        <CardContent>
          <Field label="Stage" htmlFor="stage-filter">
            <Select
              id="stage-filter"
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value as OrderStage | '')}
            >
              <option value="">— wszystkie —</option>
              {STAGE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {STAGE_LABEL[s]}
                </option>
              ))}
            </Select>
          </Field>
        </CardContent>
      </Card>

      {orders.isPending ? (
        <p className="text-sm text-text-muted">Ładowanie…</p>
      ) : (orders.data ?? []).length === 0 ? (
        <p className="rounded-md bg-surface p-4 text-sm text-text-muted ring-1 ring-black/5">
          Brak zamówień spełniających filtr.
        </p>
      ) : (
        <ul role="list" className="space-y-3">
          {(orders.data ?? []).map((o) => (
            <OperatorOrderRow key={o.id} order={o} />
          ))}
        </ul>
      )}
    </div>
  );
}

function OperatorOrderRow({ order }: { order: OperatorOrder }) {
  const qc = useQueryClient();
  const [stage, setStage] = React.useState<OrderStage>(order.stage);
  const [delivered, setDelivered] = React.useState<DeliveredUrl[]>(order.delivered_urls);
  const [newUrl, setNewUrl] = React.useState('');
  const [newDr, setNewDr] = React.useState('');
  const [newAnchor, setNewAnchor] = React.useState('');

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(
        `${env.apiBaseUrl}/api/operator/content/orders/${order.id}/stage`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ stage, delivered_urls: delivered }),
        }
      );
      if (!res.ok) throw new Error('save_failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operator', 'content-orders'] }),
  });

  const addDelivered = () => {
    if (!newUrl) return;
    setDelivered((d) => [
      ...d,
      {
        url: newUrl,
        dr: newDr ? Number(newDr) : undefined,
        anchor: newAnchor || undefined,
      },
    ]);
    setNewUrl('');
    setNewDr('');
    setNewAnchor('');
  };

  const removeDelivered = (idx: number) => {
    setDelivered((d) => d.filter((_, i) => i !== idx));
  };

  const markIndexed = (idx: number) => {
    setDelivered((d) =>
      d.map((row, i) =>
        i === idx ? { ...row, indexed_at: Math.floor(Date.now() / 1000) } : row
      )
    );
  };

  const isBacklink = order.package_kind === 'backlink_outreach';
  const landing = typeof order.metadata?.landing_page_url === 'string'
    ? order.metadata.landing_page_url
    : null;
  const anchorPrefs = Array.isArray(order.metadata?.anchor_texts)
    ? (order.metadata!.anchor_texts as string[])
    : [];

  return (
    <li className="rounded-md bg-surface p-4 ring-1 ring-black/5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-display text-base font-semibold text-text">
            {order.package_name}
          </p>
          <p className="mt-0.5 text-xs text-text-muted">
            {order.tenant_legal_name ?? order.tenant_slug ?? order.tenant_id} · Zlecone{' '}
            {new Date(order.requested_at * 1000).toLocaleString('pl-PL')} · State:{' '}
            <span className="font-mono">{STATE_LABEL[order.state]}</span>
          </p>
          {landing ? (
            <p className="mt-1 break-all text-xs text-text-muted">
              Landing: <span className="font-mono">{landing}</span>
            </p>
          ) : null}
          {anchorPrefs.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {anchorPrefs.map((a) => (
                <span
                  key={a}
                  className="rounded-full bg-bg px-2 py-0.5 text-[10px] italic text-text-muted ring-1 ring-border"
                >
                  „{a}"
                </span>
              ))}
            </div>
          ) : null}
          {order.notes ? (
            <p className="mt-2 rounded-md bg-bg p-2 text-xs text-text ring-1 ring-border">
              <strong className="font-semibold">Uwagi klienta:</strong> {order.notes}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={stage}
            onChange={(e) => setStage(e.target.value as OrderStage)}
            className="h-9"
          >
            {STAGE_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {STAGE_LABEL[s]}
              </option>
            ))}
          </Select>
          <Button
            type="button"
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            <Save className="size-3.5" aria-hidden="true" /> Zapisz
          </Button>
        </div>
      </div>

      {isBacklink ? (
        <div className="mt-4 space-y-3 border-t border-border pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">
            Dostarczone linki ({delivered.length})
          </p>
          {delivered.length > 0 ? (
            <ul role="list" className="space-y-1.5">
              {delivered.map((d, i) => (
                <li
                  key={`${d.url}-${i}`}
                  className="flex flex-wrap items-center gap-2 rounded-md bg-bg p-2 text-xs ring-1 ring-border"
                >
                  <LinkIcon className="size-3 text-text-muted" aria-hidden="true" />
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 truncate font-mono text-accent hover:underline"
                  >
                    {d.url}
                  </a>
                  {d.dr != null ? (
                    <span className="font-mono text-text-subtle">DR {d.dr}</span>
                  ) : null}
                  {d.anchor ? (
                    <span className="italic text-text-subtle">„{d.anchor}"</span>
                  ) : null}
                  {d.indexed_at ? (
                    <span className="text-success">
                      ✓ {new Date(d.indexed_at * 1000).toLocaleDateString('pl-PL')}
                    </span>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => markIndexed(i)}
                    >
                      ✓ Zaindeksowany
                    </Button>
                  )}
                  <button
                    type="button"
                    aria-label="Usuń"
                    onClick={() => removeDelivered(i)}
                    className="text-text-subtle hover:text-error"
                  >
                    <X className="size-3" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-text-muted">Brak dostarczonych linków.</p>
          )}

          <div className="grid gap-2 md:grid-cols-[2fr,80px,2fr,auto]">
            <Input
              type="url"
              placeholder="https://example.com/artykul"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
            />
            <Input
              type="number"
              placeholder="DR"
              value={newDr}
              onChange={(e) => setNewDr(e.target.value)}
            />
            <Input
              placeholder="anchor text"
              value={newAnchor}
              onChange={(e) => setNewAnchor(e.target.value)}
            />
            <Button type="button" variant="secondary" size="sm" onClick={addDelivered}>
              Dodaj link
            </Button>
          </div>
        </div>
      ) : null}
    </li>
  );
}

// Silence unused import — Textarea is reserved for a future notes editor.
void Textarea;
void cn;
